import { fromJS, Map} from 'immutable';

// returns an array of combined values for configuration and data paired arrays for ValueRealized, ConfiguredItems, UseQuestions, etc
// question expects items in dsaConfig(the configured questions, titles, etc), answer expects items in dsa (the user entries)
// question = dsaConfig.useQuestions, dsaConfig.valueRealized, dsaConfig.configuredItems
// items have ids and configured properties {id: 7, title:'Monitor capacity utilization of system resources', subarea: 2, etc.. }
// answer = dsa.dsaUseQuestions, dsa.dsaValueRealized, dsa.dsaConfiguredItems
// items have foreign key ids and answered properties {usequestion: 7, answer: 1, etc...}
// answersKey refers to the foreign key that's used in the answers object: usequestion, valuerealized, or configureditem
export const getCombinedData = (questionsRaw, answersRaw, answersKey, questionsKey = 'id') => {
  if (questionsRaw.length === 0 || answersRaw.length === 0) return [];
  if (!answersRaw[0][answersKey]) {
    if (typeof(jest) === 'undefined') {
      console.error('Invalid answers key');
    }
    return [];
  }
  // separates out custom answers that don't need to be combined
  let customAnswers = answersRaw.filter(a => !a[answersKey]);
  let defaultAnswers = answersRaw.filter(a => !!a[answersKey]);

  let questions = fromJS(questionsRaw);
  let answers = fromJS(defaultAnswers).map(a =>  a.set('answer_id', a.get('id')));

  let answersMap = answers.reduce((lookup, item) => lookup.set(item.get(answersKey), item), Map());
  let questionsMap = questions.reduce((lookup, item) => lookup.set(item.get(questionsKey), item), Map());
  let combinedMap = answersMap.mergeDeep(questionsMap);
  return combinedMap.toList().toJS().concat(customAnswers);
};

// config'd item helpers
export const totalDataPerDay = (item) => {
  let val = (item.totalItems * item.estimatedLogSize) / 1024;
  return +val.toFixed(3);
};
export const dataIndexedPerDay = (item) => {
  let val = totalDataPerDay(item) * (item.indexed / 100) * (item.percentDataSourcesIndexed / 100);
  return +val.toFixed(3);
};

export const getTotalIndexed = (items) => {
  return items.reduce((total, item) => total + dataIndexedPerDay(item), 0);
};

export const getTotalDataPerDay = (items) => {
  return items.reduce((total, item) => total + totalDataPerDay(item), 0);
};


export const percentIndexed = (stats) => stats.dataIndexed > 0 && stats.dataRequired && Math.round((stats.dataIndexed / stats.dataRequired) * 100) || 0;


// Fraction (0..1) of an item's data attributable to Cisco, derived from
// the ciscoPercent field (0–100). Returns 0 if unset.
export const getCiscoRatio = (item) => {
  if (item.ciscoPercent !== undefined && item.ciscoPercent !== null && item.ciscoPercent !== '') {
    const pct = Number(item.ciscoPercent);
    if (!isNaN(pct)) {
      return Math.max(0, Math.min(100, pct)) / 100;
    }
  }
  return 0;
};

export const getCombinedConfiguredItemStats = (items, peakBuffer = 0.2) => {
  let stats = {
    configuredItems: 0,
    dataRequired: 0,
    dataIndexed: 0,
    percentIndexed: 0,
    ciscoDataPerDay: 0,
    nonCiscoDataPerDay: 0,
    ciscoDataIndexed: 0,
    nonCiscoDataIndexed: 0
  };

  items.forEach(item => {
    const itemDataPerDay = totalDataPerDay(item);
    const itemDataIndexed = dataIndexedPerDay(item);
    const totalItems = item.totalItems || 0;
    const ciscoRatio = getCiscoRatio(item);

    stats.configuredItems += totalItems;
    stats.dataRequired += itemDataPerDay;
    stats.dataIndexed += itemDataIndexed;

    stats.ciscoDataPerDay += itemDataPerDay * ciscoRatio;
    stats.nonCiscoDataPerDay += itemDataPerDay * (1 - ciscoRatio);
    stats.ciscoDataIndexed += itemDataIndexed * ciscoRatio;
    stats.nonCiscoDataIndexed += itemDataIndexed * (1 - ciscoRatio);
  });

  stats.ciscoDataPerDay = +stats.ciscoDataPerDay.toFixed(3);
  stats.nonCiscoDataPerDay = +stats.nonCiscoDataPerDay.toFixed(3);
  stats.ciscoDataIndexed = +stats.ciscoDataIndexed.toFixed(3);
  stats.nonCiscoDataIndexed = +stats.nonCiscoDataIndexed.toFixed(3);
  stats.percentIndexed = percentIndexed(stats);
  stats.ciscoPercentIndexed = percentIndexed({
    dataIndexed: stats.ciscoDataIndexed,
    dataRequired: stats.ciscoDataPerDay
  });
  stats.nonCiscoPercentIndexed = percentIndexed({
    dataIndexed: stats.nonCiscoDataIndexed,
    dataRequired: stats.nonCiscoDataPerDay
  });
  stats.peakBuffer = peakBuffer;
  stats.environmentDataPerDay = stats.dataRequired * (1 + peakBuffer);

  return stats;
};

// Returns items that are answered and enabled
export const getValidConfiguredItems = (items) => items.filter(item => !item.excluded && item.completed);



// WEBPACK FOOTER //
// ./src/utils/statsUtils.js